import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { API } from "../../lib/api";
import { SeoHead, articleJsonLd } from "../common/SeoHead";
import { ShareRow } from "../common/ShareRow";
import { GrowthFactorCTA } from "../common/GrowthFactorCTA";
import { articleImgFallback, onImgError } from "../../lib/images";

const GF_BUSINESS_RE = /business|bisnes|perniagaan|sme|pks|growth|brand|market|sales|jual|pricing|harga|cashflow|entrepreneur|usahawan|strategy|strategi|leadership|kepimpinan|consult|perunding|training|latihan/i;

const gfEligible = (a) =>
  a.vertical === "consulting" &&
  (a.growth_factor_cta || "auto") !== "hide" &&
  GF_BUSINESS_RE.test(`${a.category || ""} ${a.title || ""}`);

const inline = (text) => {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => (i % 2 === 1 ? <strong key={i}>{p}</strong> : p));
};

const Body = ({ body, vertical }) => (
  <div className="space-y-5" data-testid="article-body">
    {(body || "").split(/\n{2,}/).map((block, i) => {
      const b = block.trim();
      if (!b) return null;
      if (b.startsWith("### ")) return <h3 key={i} className="pt-4 font-serif text-xl text-ink">{inline(b.slice(4))}</h3>;
      if (b.startsWith("## ")) return <h2 key={i} className="pt-6 font-serif text-2xl text-ink">{inline(b.slice(3))}</h2>;
      if (b.startsWith("# ")) return <h2 key={i} className="pt-6 font-serif text-2xl text-ink">{inline(b.slice(2))}</h2>;
      if (/^[-*] /m.test(b)) {
        return (
          <ul key={i} className="list-disc space-y-2 pl-6 text-base leading-relaxed text-ink/75">
            {b.split("\n").filter((l) => /^[-*] /.test(l.trim())).map((l, j) => (
              <li key={j}>{inline(l.trim().slice(2))}</li>
            ))}
          </ul>
        );
      }
      const img = b.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (img) {
        return (
          <figure key={i} className="py-2">
            <img src={img[2]} alt={img[1]} loading="lazy" onError={onImgError(vertical)} className="w-full object-cover" data-testid="article-inline-image" />
            <figcaption className="mt-2 text-center text-xs italic text-ink/50">{img[1]}</figcaption>
          </figure>
        );
      }
      return <p key={i} className="text-base leading-relaxed text-ink/75">{inline(b)}</p>;
    })}
  </div>
);

export default function ArticleReader({ vertical, basePath, channelName }) {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setArticle(null);
    setMissing(false);
    fetch(`${API}/content/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setArticle)
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <main className="container-site py-40 text-center" data-testid="article-missing">
        <p className="font-serif text-3xl text-ink">This piece isn&apos;t available.</p>
        <Link to={basePath} className="link-arrow mt-6">Back to {channelName} <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /></Link>
      </main>
    );
  }
  if (!article) return <main className="container-site py-40"><p className="text-sm text-ink/50">Loading…</p></main>;

  return (
    <main data-testid="article-reader">
      <SeoHead
        title={`${article.seo_title || article.title} | iINSPIRE`}
        description={article.meta_description || article.excerpt || article.title}
        path={`${basePath}/${article.slug}`}
        type="article"
        image={article.featured_image || undefined}
        jsonLd={[articleJsonLd({ title: article.title, description: article.meta_description || article.excerpt, slug: article.slug, basePath, author: article.author, date: article.publish_date, image: article.featured_image })]}
      />
      <article className="bg-warmwhite py-20 lg:py-28">
        <div className="container-site max-w-3xl">
          <Link to={basePath} className="link-arrow" data-testid="article-back">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {channelName}
          </Link>
          {article.category && <p className="eyebrow mt-10" data-testid="article-category">{article.category}</p>}
          <h1 className="mt-4 font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="article-title">
            {article.title}
          </h1>
          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/50" data-testid="article-byline">
            Written by {article.author || "Dr. Irma"}
            {article.publish_date ? ` · ${article.publish_date}` : ""}
          </p>
          {article.featured_image ? (
            <img src={article.featured_image} alt={article.alt_text || article.title} loading="lazy" onError={onImgError(article.vertical)} className="mt-10 w-full object-cover" data-testid="article-hero-image" />
          ) : (
            <img src={articleImgFallback(article.vertical)} alt={article.title} loading="lazy" className="mt-10 w-full object-cover" data-testid="article-hero-image" />
          )}
          {(() => {
            const eligible = gfEligible(article);
            const blocks = (article.body || "").split(/\n{2,}/).filter((b) => b.trim());
            const half = Math.floor(blocks.length / 2);
            return (
              <>
                {eligible && blocks.length >= 14 ? (
                  <>
                    <div className="mt-12"><Body body={blocks.slice(0, half).join("\n\n")} vertical={article.vertical} /></div>
                    <GrowthFactorCTA articleSlug={article.slug} category={article.category} position="article-mid" />
                    <div className="mt-12"><Body body={blocks.slice(half).join("\n\n")} vertical={article.vertical} /></div>
                  </>
                ) : (
                  <div className="mt-12"><Body body={article.body} vertical={article.vertical} /></div>
                )}
                {eligible && <GrowthFactorCTA articleSlug={article.slug} category={article.category} position="article-end" />}
              </>
            );
          })()}
          <div className="mt-12">
            <ShareRow title={article.title} testId="share" />
          </div>
          <div className="mt-16 border-t border-ink/10 pt-10">
            <p className="font-serif text-2xl italic text-ink">Let&apos;s Inspire Possibilities.</p>
            <Link to="/contact" className="btn-primary mt-6" data-testid="article-contact-cta">
              Work With Us <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}
